import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import "./editable-tag-list-cell.scss";

export interface EditableTagListCellValue {
  values: string[];
  placeholder?: string;
  emptyLabel?: string;
  /** Case-insensitive dedupe by default. */
  caseInsensitiveDedupe?: boolean;
  disabled?: boolean;
}

export interface EditableTagListCellProps {
  value: EditableTagListCellValue;
  rowData?: any;
  header?: string;
  onCellValueChange?: (rowData: any, fieldKey: string, newValue: any) => void;
  /**
   * Max tags to show before collapsing the rest behind a "+N" badge.
   * When the cell is not disabled, click the badge to show every tag so you can add, remove, or scan the full list; use "Show less" to collapse again.
   */
  maxVisibleTags?: number;
  className?: string;
}

function CloseGlyph({ size = 14, color = "#0f0f0f" }: { size?: number; color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 4L4 10M4 4L10 10"
        stroke={color}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlusGlyph({ size = 16, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8 3.333V12.667M3.333 8H12.667"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const EditableTagListCell: React.FC<EditableTagListCellProps> = ({
  value,
  rowData,
  header,
  onCellValueChange,
  maxVisibleTags,
  className = "",
}) => {
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [editingTagIndex, setEditingTagIndex] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [tags, setTags] = useState<string[]>(value.values || []);
  const [overflowExpanded, setOverflowExpanded] = useState(false);

  useEffect(() => {
    setTags(value.values || []);
    setEditingTagIndex(null);
    setEditDraft("");
  }, [value.values]);

  const placeholder = value.placeholder || "Add";
  const dedupe = value.caseInsensitiveDedupe ?? true;

  const shouldTruncate =
    maxVisibleTags !== undefined && maxVisibleTags >= 0 && tags.length > maxVisibleTags;
  const visibleTags = shouldTruncate ? tags.slice(0, maxVisibleTags) : tags;
  const hiddenTags = shouldTruncate ? tags.slice(maxVisibleTags) : [];
  const hiddenTagsText = hiddenTags.join(", ");
  const useVerticalOverflowTooltip = hiddenTagsText.length > 200;

  useEffect(() => {
    if (!shouldTruncate) setOverflowExpanded(false);
  }, [shouldTruncate]);

  /** Collapsed chip row when truncated; expanded (or not truncated) shows every tag for editing. */
  const showCollapsedOverflow =
    shouldTruncate && (value.disabled || !overflowExpanded);
  const tagsToRender = showCollapsedOverflow ? visibleTags : tags;

  const overflowAnchorRef = useRef<HTMLSpanElement>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const showTooltip = useCallback(() => {
    if (!overflowAnchorRef.current) return;
    const rect = overflowAnchorRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom + 6,
      left: rect.left + rect.width / 2,
    });
    setTooltipVisible(true);
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltipVisible(false);
  }, []);

  const updateTags = (next: string[]) => {
    setTags(next);
    onCellValueChange?.(rowData, header || "", next);
  };

  const handleRemove = (tag: string) => {
    updateTags(tags.filter((t) => t !== tag));
  };

  const handleAdd = () => {
    const trimmed = draft.trim();
    setDraft("");
    setEditing(false);
    if (!trimmed) return;
    const exists = tags.some((t) =>
      dedupe ? t.toLowerCase() === trimmed.toLowerCase() : t === trimmed,
    );
    if (exists) return;
    updateTags([...tags, trimmed]);
  };

  const cancelTagEdit = () => {
    setEditingTagIndex(null);
    setEditDraft("");
  };

  const startTagEdit = (index: number) => {
    const tag = tags[index];
    if (tag === undefined) return;
    setEditing(false);
    setDraft("");
    setEditingTagIndex(index);
    setEditDraft(tag);
  };

  const commitTagEdit = () => {
    if (editingTagIndex === null) return;
    const index = editingTagIndex;
    const trimmed = editDraft.trim();
    cancelTagEdit();
    if (!trimmed) return;
    const current = tags[index];
    if (current === undefined || trimmed === current) return;
    const otherTags = tags.filter((_, i) => i !== index);
    const exists = otherTags.some((t) =>
      dedupe ? t.toLowerCase() === trimmed.toLowerCase() : t === trimmed,
    );
    if (exists) return;
    const next = [...tags];
    next[index] = trimmed;
    updateTags(next);
  };

  return (
    <div
      className={`editable-tag-list ${className}`.trim()}
      onClick={(e) => e.stopPropagation()}
    >
      {tags.length === 0 && value.disabled && value.emptyLabel && (
        <span className="editable-tag-list__empty">{value.emptyLabel}</span>
      )}

      {tagsToRender.map((v, index) => (
        <span
          key={`${index}-${v}`}
          className={`editable-tag-list__tag${
            editingTagIndex === index ? " editable-tag-list__tag--editing" : ""
          }`}
          title={editingTagIndex === index || v.length <= 30 ? undefined : v}
        >
          {!value.disabled && editingTagIndex === index ? (
            <input
              autoFocus
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              onBlur={commitTagEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitTagEdit();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  cancelTagEdit();
                }
              }}
              aria-label={`Edit tag ${v}`}
              className="editable-tag-list__tag-input"
            />
          ) : value.disabled ? (
            <span className="editable-tag-list__tag-label">
              {v.length > 30 ? `${v.slice(0, 30)}...` : v}
            </span>
          ) : (
            <button
              type="button"
              className="editable-tag-list__tag-label editable-tag-list__tag-label--editable"
              aria-label={`Edit tag ${v}`}
              onClick={(e) => {
                e.stopPropagation();
                startTagEdit(index);
              }}
            >
              {v.length > 30 ? `${v.slice(0, 30)}...` : v}
            </button>
          )}
          {!value.disabled && editingTagIndex !== index && (
            <button
              type="button"
              aria-label={`Remove ${v}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(v);
              }}
              className="editable-tag-list__remove-btn"
            >
              <CloseGlyph />
            </button>
          )}
        </span>
      ))}

      {hiddenTags.length > 0 && showCollapsedOverflow && (
        <>
          <span
            ref={overflowAnchorRef}
            className="editable-tag-list__overflow-anchor"
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
          >
            {value.disabled ? (
              <span
                className="editable-tag-list__overflow"
                aria-label={`${hiddenTags.length} more: ${hiddenTags.join(", ")}`}
              >
                +{hiddenTags.length}
              </span>
            ) : (
              <button
                type="button"
                className="editable-tag-list__overflow editable-tag-list__overflow--interactive"
                aria-expanded={overflowExpanded}
                aria-label={`Show ${hiddenTags.length} more tags: ${hiddenTags.join(", ")}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setOverflowExpanded(true);
                }}
              >
                +{hiddenTags.length}
              </button>
            )}
          </span>
          {tooltipVisible &&
            createPortal(
              <span
                className={`editable-tag-list__overflow-tooltip${
                  useVerticalOverflowTooltip
                    ? " editable-tag-list__overflow-tooltip--vertical"
                    : ""
                }`}
                style={{ top: tooltipPos.top, left: tooltipPos.left }}
              >
                {useVerticalOverflowTooltip
                  ? hiddenTags.map((tag, tagIndex) => (
                      <span
                        key={`${tagIndex}-${tag}`}
                        className="editable-tag-list__overflow-tooltip-item"
                      >
                        {tag}
                      </span>
                    ))
                  : hiddenTagsText}
              </span>,
              document.body,
            )}
        </>
      )}

      {shouldTruncate && !value.disabled && overflowExpanded && (
        <button
          type="button"
          className="editable-tag-list__show-less"
          aria-label="Show fewer tags"
          onClick={(e) => {
            e.stopPropagation();
            setOverflowExpanded(false);
          }}
        >
          Show less
        </button>
      )}

      {value.disabled || editingTagIndex !== null ? null : editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleAdd}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
            if (e.key === "Escape") {
              setDraft("");
              setEditing(false);
            }
          }}
          placeholder={placeholder}
          className="editable-tag-list__input"
        />
      ) : (
        <button
          type="button"
          aria-label="Add tag"
          onClick={(e) => {
            e.stopPropagation();
            cancelTagEdit();
            setEditing(true);
          }}
          className="editable-tag-list__add-btn"
        >
          <PlusGlyph />
        </button>
      )}
    </div>
  );
};

export default EditableTagListCell;
